import { useMemo } from 'react';
import { X } from '@phosphor-icons/react';
import { Flex, MultiSelectComboBox, SettingsSection, Text, type MultiSelectOption } from '@chatsdet/ui';
import { useProductMap, useSetWorkflowCapabilities, useWorkflowCapabilities } from '../../../features/product-map/useProductMap';
import type { WorkflowCapabilityOption } from '../../../features/product-map/types';

interface CapabilityOption extends MultiSelectOption {
  title: string;
  areaTitle: string | null;
  /** `null` only for a capability in the Unassigned bucket, which has no code. */
  code: string | null;
}

function toOption(
  capability: Pick<WorkflowCapabilityOption, 'capabilityId' | 'title' | 'areaTitle' | 'code'>
): CapabilityOption {
  return {
    id: capability.capabilityId,
    label: capability.areaTitle ? `${capability.title} · ${capability.areaTitle}` : capability.title,
    title: capability.title,
    areaTitle: capability.areaTitle,
    code: capability.code,
  };
}

type Props = {
  workflowId: string;
  /** The flow's resolved property (raw UUID or `prop_…`), when known — options
   *  are sourced from this property's product map. `null` for a login-less
   *  flow with no resolvable property, in which case the picker is disabled. */
  propertyId: string | null;
};

export function CapabilityLinkSettings({ workflowId, propertyId }: Props) {
  const linkedQuery = useWorkflowCapabilities(workflowId);
  const mapQuery = useProductMap(propertyId ?? undefined);
  const setCapabilities = useSetWorkflowCapabilities(workflowId);

  const linked = linkedQuery.data ?? [];

  // Every capability arrives with its own `code` — both the map (for the
  // searchable option list) and `getWorkflowCapabilities` (for what's already
  // linked) select it server-side. Nothing is derived by cross-referencing the
  // two, so a capability linked from another application still shows its code.
  const options: CapabilityOption[] = useMemo(() => {
    if (!mapQuery.data) return linked.map(toOption);
    const byId = new Map<string, CapabilityOption>();
    for (const area of mapQuery.data.areas) {
      for (const capability of area.capabilities) {
        byId.set(
          capability.id,
          toOption({
            capabilityId: capability.id,
            title: capability.title,
            areaTitle: area.title,
            code: capability.code,
          })
        );
      }
    }
    for (const capability of mapQuery.data.unassignedCapabilities) {
      byId.set(
        capability.id,
        toOption({
          capabilityId: capability.id,
          title: capability.title,
          areaTitle: null,
          code: capability.code,
        })
      );
    }
    // A currently-linked capability filed under a different property than
    // `propertyId` still needs to render as a removable option.
    for (const capability of linked) {
      if (!byId.has(capability.capabilityId)) byId.set(capability.capabilityId, toOption(capability));
    }
    return Array.from(byId.values());
  }, [mapQuery.data, linked]);

  const selected: CapabilityOption[] = useMemo(() => linked.map(toOption), [linked]);

  const sortedLinked = useMemo(
    () => [...selected].sort((a, b) => a.title.localeCompare(b.title)),
    [selected]
  );

  const isDisabled = !propertyId || setCapabilities.isPending;

  const handleChange = (items: CapabilityOption[]) => {
    setCapabilities.mutate(items.map((item) => item.id));
  };

  const handleRemove = (capabilityId: string) => {
    setCapabilities.mutate(linked.filter((c) => c.capabilityId !== capabilityId).map((c) => c.capabilityId));
  };

  return (
    <SettingsSection
      title="Product capabilities"
      description="Capabilities this flow covers, from the Product Map."
    >
      <MultiSelectComboBox<CapabilityOption>
        aria-label="Search capabilities to link"
        placeholder={propertyId ? 'Search capabilities to link…' : 'No property resolved for this flow'}
        items={options}
        value={selected}
        onChange={handleChange}
        isLoading={linkedQuery.isLoading || mapQuery.isLoading}
        isDisabled={isDisabled}
        // No inline chips inside the trigger — linked capabilities render as
        // the list below instead, with room for full (non-truncated) titles.
        hideSelectedTags
        renderItem={(item) => (
          <div className="flex min-w-0 flex-col">
            <Flex align="center" gap="sm" className="min-w-0">
              {item.code && (
                <Text size="xs" className="shrink-0 font-mono text-muted-foreground/60">
                  {item.code}
                </Text>
              )}
              <span className="min-w-0 text-pretty line-clamp-2">{item.title}</span>
            </Flex>
            {item.areaTitle && (
              <span className="truncate text-xs text-muted-foreground">{item.areaTitle}</span>
            )}
          </div>
        )}
      />

      {sortedLinked.length > 0 && (
        <ul className="space-y-0.5">
          {sortedLinked.map((capability) => (
            <li
              key={capability.id}
              className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/50"
            >
              {capability.code && (
                <Text size="xs" className="shrink-0 font-mono text-muted-foreground/60">
                  {capability.code}
                </Text>
              )}
              <Text size="sm" className="min-w-0 flex-1 text-pretty line-clamp-2">
                {capability.title}
                {capability.areaTitle && (
                  <span className="text-muted-foreground"> · {capability.areaTitle}</span>
                )}
              </Text>
              <button
                type="button"
                onClick={() => handleRemove(capability.id)}
                disabled={setCapabilities.isPending}
                aria-label={`Unlink ${capability.title}`}
                className="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {!propertyId && (
        <Text size="xs" className="text-muted-foreground">
          Add a login node so this flow resolves to a property before linking capabilities.
        </Text>
      )}
    </SettingsSection>
  );
}
